import type { ErrorCode, PlatformPermission } from "@cafe/shared";
import { ERROR_MESSAGES_RU, platformCan } from "@cafe/shared";
import { useSyncExternalStore } from "react";

export class ApiError extends Error {
  constructor(readonly status: number, readonly code: ErrorCode | "NETWORK_ERROR", message: string, readonly details?: Record<string, unknown> & { fields?: { path: string; message: string }[] }) {
    super(message);
  }
  get fields() {
    return this.details?.fields ?? [];
  }
}

export type Me = { id: string; name: string; email?: string; role: string };
type Session = { token: string; user: Me; expiresAt: string } | null;
const KEY = "cafe.platform.session";

/** Сессия сотрудника платформы (8 ч) — sessionStorage: не переживает закрытие вкладки. */
let session: Session = (() => {
  try {
    const s = JSON.parse(sessionStorage.getItem(KEY) ?? "null") as Session;
    return s && new Date(s.expiresAt) > new Date() ? s : null;
  } catch {
    return null;
  }
})();
const listeners = new Set<() => void>();
export const auth = {
  get: () => session,
  set(s: Session) {
    session = s;
    try {
      if (s) sessionStorage.setItem(KEY, JSON.stringify(s));
      else sessionStorage.removeItem(KEY);
    } catch {
      /* приватный режим */
    }
    listeners.forEach((l) => l());
  },
};
export const useSession = () => useSyncExternalStore((l) => (listeners.add(l), () => listeners.delete(l)), () => session);
export const useCan = () => {
  const s = useSession();
  return (p: PlatformPermission) => !!s && platformCan(s.user.role, p);
};

export async function api<T>(method: string, path: string, body?: unknown, opts: { withMeta?: boolean; query?: Record<string, string | number | undefined> } = {}): Promise<T> {
  const url = new URL(`/platform/v1${path}`, window.location.origin);
  for (const [k, v] of Object.entries(opts.query ?? {})) if (v !== undefined && v !== "") url.searchParams.set(k, String(v));
  let res: Response;
  try {
    res = await fetch(url, {
      method,
      headers: { Accept: "application/json", ...(body !== undefined && { "Content-Type": "application/json" }), ...(session && { Authorization: `Bearer ${session.token}` }) },
      body: body === undefined ? undefined : JSON.stringify(body),
    });
  } catch {
    throw new ApiError(0, "NETWORK_ERROR", "Нет связи с сервером");
  }
  if (res.status === 204) return undefined as T;
  const json = (await res.json().catch(() => null)) as { success: boolean; data?: T; meta?: unknown; error?: { code: ErrorCode; message: string; details?: Record<string, unknown> } } | null;
  if (res.ok && json?.success) return (opts.withMeta ? { items: json.data, meta: json.meta } : json.data) as T;
  const code = json?.error?.code ?? "INTERNAL_ERROR";
  if (res.status === 401 && session && code !== "INVALID_CREDENTIALS" && code !== "TWO_FACTOR_INVALID") auth.set(null);
  throw new ApiError(res.status, code, json?.error?.message ?? ERROR_MESSAGES_RU[code] ?? "Ошибка запроса", json?.error?.details);
}

export const http = {
  get: <T>(path: string, query?: Record<string, string | number | undefined>) => api<T>("GET", path, undefined, { query }),
  post: <T>(path: string, body: unknown = {}) => api<T>("POST", path, body),
  put: <T>(path: string, body: unknown) => api<T>("PUT", path, body),
  patch: <T>(path: string, body: unknown) => api<T>("PATCH", path, body),
  del: <T>(path: string) => api<T>("DELETE", path),
};

export const errorText = (e: unknown) => (e instanceof ApiError ? e.fields.map((f) => f.message).join("; ") || e.message : "Ошибка");

/** Деньги: minor → «4 500 сом» */
export const money = (minor: number, currency = "KGS") => {
  const sym: Record<string, string> = { KGS: "сом", KZT: "₸", UZS: "сум", RUB: "₽", USD: "$" };
  return `${new Intl.NumberFormat("ru-RU", { maximumFractionDigits: 2 }).format(minor / 100)} ${sym[currency] ?? currency}`;
};
export const dt = (iso: string | null | undefined) => (iso ? new Intl.DateTimeFormat("ru-RU", { dateStyle: "short", timeStyle: "short" }).format(new Date(iso)) : "—");
export const STATUS: Record<string, { label: string; tone: "success" | "warning" | "danger" | "info" | "neutral" }> = {
  trialing: { label: "Пробный период", tone: "info" }, active: { label: "Активна", tone: "success" }, past_due: { label: "Ожидает оплаты", tone: "warning" },
  read_only: { label: "Только просмотр", tone: "danger" }, suspended: { label: "Приостановлена", tone: "danger" }, cancelled: { label: "Отменена", tone: "warning" },
  expired: { label: "Истекла", tone: "danger" }, archived: { label: "В архиве", tone: "neutral" },
};
export const INVOICE_STATUS: Record<string, { label: string; tone: "success" | "warning" | "danger" | "info" | "neutral" }> = {
  open: { label: "Ожидает оплаты", tone: "warning" }, paid: { label: "Оплачен", tone: "success" }, void: { label: "Аннулирован", tone: "neutral" }, refunded: { label: "Возврат", tone: "danger" }, draft: { label: "Черновик", tone: "neutral" },
};
export const ROLES: Record<string, string> = { owner: "Владелец", admin: "Администратор", billing: "Бухгалтер (счета)", sales: "Продажи", support: "Поддержка", auditor: "Проверяющий (только просмотр)" };
export const day = (iso: string | null | undefined) => (iso ? new Intl.DateTimeFormat("ru-RU", { dateStyle: "short", timeZone: iso.length === 10 ? "UTC" : undefined }).format(new Date(iso)) : "—");
/** «2026-10» → «окт. 2026» */
export const monthLabel = (ym: string, short = false) => new Intl.DateTimeFormat("ru-RU", { month: short ? "short" : "long", year: short ? "2-digit" : "numeric", timeZone: "UTC" }).format(new Date(`${ym}-01T00:00:00Z`));
export const pct = (v: number | null | undefined) => (v === null || v === undefined ? "—" : `${v > 0 ? "+" : ""}${Math.round(v * 1000) / 10}%`);
